/**
 * toasts_controller.js — global toast/notification UI (Stimulus).
 *
 * Element-scoped owner of the app's transient toasts. Attached to a persistent
 * (data-turbo-permanent) #toast-root container in base.html / public_base.html,
 * so it connects once and survives Turbo <body> swaps. Listens on the persistent
 * document for `toasts:show` / `toasts:dismiss` events dispatched by the
 * window.showNotification* compatibility shims in notifications.js, and renders a
 * .unified-notification. Positioning/appearance live in static/css/notifications.css.
 *
 * Replaces the former global DOM-creating service: nothing on window builds DOM,
 * and there is no inline onclick. Swipe-to-the-side-to-dismiss (Pointer Events,
 * so touch + mouse + pen) is owned here; the horizontal drag is appended to the
 * base transform so it composes with the CSS centering instead of clobbering it.
 *
 * One toast at a time: showing a new one removes the current (mirrors the prior
 * behaviour where showNotification cleared existing .unified-notification nodes).
 */

import { Controller } from "@hotwired/stimulus";

    var DEFAULT_DURATION = 5000;
    var DISMISS_THRESHOLD = 60; // px dragged sideways before it dismisses
    var TAP_SLOP = 8;           // px of drag a tap may wobble and still count as a tap
    var FADE_OVER = 200;        // px of drag that fades opacity to 0
    // Swipe exit is linear so the toast carries on at roughly flick speed
    // (~450px on a phone in 250ms). ease-out front-loads the distance and the
    // fade, so the exit reads as a one- or two-frame jump.
    var SWIPE_EXIT_MS = 250;

    var STYLES = {
        success: { bgClass: 'alert-success', icon: 'bi-check-circle-fill', color: '#0f5132' },
        error:   { bgClass: 'alert-danger',  icon: 'bi-exclamation-triangle-fill', color: '#842029' },
        warning: { bgClass: 'alert-warning', icon: 'bi-exclamation-triangle-fill', color: '#664d03' },
        info:    { bgClass: 'alert-info',    icon: 'bi-info-circle-fill', color: '#055160' }
    };

export default class extends Controller {
        connect() {
            // An event carrying detail is a legacy shim (bundled copies still
            // dispatch {detail}); render it directly. A bare event is the
            // queue-based shim (notifications.js) — drain the replay queue. Also
            // drain on connect so a toast requested before this controller
            // attached (flash-on-load / permanent-host transfer races) is shown
            // rather than dropped.
            this._onShow = (e) => {
                if (e.detail && e.detail.message != null) this.show(e.detail);
                else this._drain();
            };
            this._onDismiss = () => this.dismiss();
            document.addEventListener('toasts:show', this._onShow);
            document.addEventListener('toasts:dismiss', this._onDismiss);
            this._drain();
        }

        // Replay queued toasts. One toast shows at a time, so the most recent
        // request wins and the rest are discarded.
        _drain() {
            var q = window.__toastQueue;
            if (!q || !q.length) return;
            var detail = q[q.length - 1];
            window.__toastQueue = [];
            this.show(detail);
        }

        disconnect() {
            document.removeEventListener('toasts:show', this._onShow);
            document.removeEventListener('toasts:dismiss', this._onDismiss);
            // Deliberately do NOT clear the dismiss timer or remove the current
            // toast. #toast-root is data-turbo-permanent, so Stimulus bounces this
            // controller (disconnect -> reconnect) on EVERY Turbo navigation as the
            // node is moved into the new <body>. The single most common moment a
            // toast is on screen is the instant right after a navigation — a server
            // flash() (e.g. the /contact "Thanks for your message" confirmation),
            // which the live listener renders synchronously during the swap. If we
            // tore it down here, that microtask-later disconnect would delete the
            // toast we just showed — which is exactly why flash toasts never
            // appeared. The toast keeps its own setTimeout and node-local
            // swipe/close listeners, so it still auto-dismisses on its own. This
            // host is only ever permanent, so the controller never truly tears
            // down; there is nothing to leak.
        }

        show(detail) {
            var type = detail.type || 'info';
            var duration = detail.duration || DEFAULT_DURATION;
            var style = STYLES[type] || STYLES.info;

            this.dismiss(true); // single toast at a time

            var n = document.createElement('div');
            n.className = 'unified-notification alert ' + style.bgClass +
                ' d-flex align-items-center alert-dismissible';
            // Deliberately NOT data-turbo-temporary. Turbo's CacheObserver
            // implements that attribute by removing the node from the LIVE
            // document on turbo:before-cache, not from the snapshot copy — so a
            // toast shown immediately before a navigation (the "Job deleted
            // successfully" that precedes Turbo.visit('/jobs'), every flash that
            // fires next to a redirect) was destroyed ~50ms in, mid slide-in.
            // It is also unnecessary: #toast-root is data-turbo-permanent, and
            // Turbo always discards the incoming copy of a permanent element in
            // favour of the live one, so a cached snapshot can never resurrect a
            // stale toast. The node's own timer is what ends it.
            // Static markup only via innerHTML; the message is user-controlled
            // (e.g. business/display names concatenated into notification titles),
            // so it goes in via textContent to keep it out of the HTML sink.
            n.innerHTML =
                '<i class="bi ' + style.icon + ' me-2" style="color: ' + style.color + ';"></i>' +
                '<span class="flex-grow-1"></span>' +
                '<button type="button" class="btn-close" aria-label="Close"></button>';
            n.querySelector('.flex-grow-1').textContent = (detail.message || '');

            this.element.appendChild(n);
            this._current = n;

            n.querySelector('.btn-close').addEventListener('click', () => this.dismiss());
            this._wireSwipe(n);
            if (detail.url) this._wireLink(n, detail.url);

            this._timer = setTimeout(() => this.dismiss(), duration);
        }

        // immediate=true removes without the slide-out animation (used when a new
        // toast replaces the current one).
        dismiss(immediate) {
            this._clearTimer();
            var n = this._current;
            if (!n) return;
            this._current = null;
            if (immediate) {
                n.remove();
                return;
            }
            var exit = window.matchMedia('(max-width: 576px)').matches ? 'slideOutUpMobile' : 'slideOutUp';
            n.style.animation = exit + ' 0.3s ease-out forwards';
            setTimeout(() => { if (n.parentNode) n.remove(); }, 300);
        }

        _clearTimer() {
            if (this._timer) {
                clearTimeout(this._timer);
                this._timer = null;
            }
        }

        // A toast that names a record opens it when tapped. The close button
        // keeps its own job, and a sideways drag is a dismiss gesture, not a tap
        // (_wireSwipe marks it on the node before the click fires).
        _wireLink(n, url) {
            n.style.cursor = 'pointer';
            n.addEventListener('click', (e) => {
                if (e.target.closest('.btn-close') || n.dataset.dragged) return;
                this.dismiss(true);
                window.Turbo.visit(url);
            });
        }

        // Pointer-driven swipe-to-the-side dismiss. Listeners live on the toast
        // element and are released when it is removed from the DOM.
        _wireSwipe(n) {
            var startX = 0;
            var deltaX = 0;
            var dragging = false;

            // Mirror the settled horizontal transform from notifications.css:
            // centered on desktop, none on the mobile breakpoint.
            var base = () =>
                window.matchMedia('(max-width: 576px)').matches ? '' : 'translateX(-50%)';

            // `important` so it overrides the `transform: none !important` mobile
            // media-query rule; a plain inline style cannot.
            var setTransform = (value) =>
                n.style.setProperty('transform', value.trim(), 'important');

            n.addEventListener('pointerdown', (e) => {
                if (e.target.closest('.btn-close')) return; // let the close button work
                startX = e.clientX;
                deltaX = 0;
                dragging = true;
                n.style.transition = 'none';
                n.style.animation = 'none';
                n.setPointerCapture(e.pointerId);
            });

            n.addEventListener('pointermove', (e) => {
                if (!dragging) return;
                deltaX = e.clientX - startX;
                setTransform(base() + ' translateX(' + deltaX + 'px)');
                n.style.opacity = String(Math.max(0, 1 - Math.abs(deltaX) / FADE_OVER));
            });

            var end = () => {
                if (!dragging) return;
                dragging = false;
                n.dataset.dragged = Math.abs(deltaX) > TAP_SLOP ? '1' : '';
                if (Math.abs(deltaX) > DISMISS_THRESHOLD) {
                    this._clearTimer();
                    if (this._current === n) this._current = null;
                    n.style.transition = 'transform ' + SWIPE_EXIT_MS + 'ms linear, opacity ' + SWIPE_EXIT_MS + 'ms linear';
                    setTransform(base() + ' translateX(' + (deltaX > 0 ? '150%' : '-150%') + ')');
                    n.style.opacity = '0';
                    setTimeout(() => { if (n.parentNode) n.remove(); }, SWIPE_EXIT_MS);
                } else {
                    n.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out';
                    setTransform(base()); // snap back
                    n.style.opacity = '1';
                }
            };

            n.addEventListener('pointerup', end);
            n.addEventListener('pointercancel', end);
        }
}
